/**
 * Connection Reliability Module
 * 
 * Phase 1: Connection Reliability
 * 
 * Features:
 * - Exponential backoff with jitter
 * - Connection quality monitoring
 * - Circuit breaker pattern
 * - Adaptive reconnection strategy
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Circuit Breaker States
 */
export const CircuitState = {
  CLOSED: 'CLOSED',       // Normal operation
  OPEN: 'OPEN',           // Too many failures, stop trying
  HALF_OPEN: 'HALF_OPEN'  // Testing if service recovered
};

/**
 * Connection Quality Levels
 */
export const ConnectionQuality = {
  EXCELLENT: 'EXCELLENT',  // < 100ms latency, 0% packet loss
  GOOD: 'GOOD',            // < 300ms latency, < 5% packet loss
  FAIR: 'FAIR',            // < 1000ms latency, < 20% packet loss
  POOR: 'POOR'             // > 1000ms latency or > 20% packet loss
};

/**
 * Circuit Breaker for WebSocket connections
 * 
 * Prevents excessive reconnection attempts when service is down.
 * Uses three states: CLOSED (normal), OPEN (failing), HALF_OPEN (testing)
 */
export class CircuitBreaker {
  constructor(options = {}) {
    this.failureThreshold = options.failureThreshold || 5;
    this.successThreshold = options.successThreshold || 2;
    this.timeout = options.timeout || 60000; // 1 minute
    
    this.state = CircuitState.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.nextAttempt = Date.now();
    this.lastStateChange = Date.now();
  }

  /**
   * Check if request should be allowed
   */
  canAttempt() {
    if (this.state === CircuitState.CLOSED) {
      return true;
    }
    
    if (this.state === CircuitState.OPEN) {
      // Check if timeout has passed
      if (Date.now() >= this.nextAttempt) {
        console.log('🔄 Circuit breaker: Transitioning to HALF_OPEN (testing recovery)');
        this.state = CircuitState.HALF_OPEN;
        this.successCount = 0;
        this.lastStateChange = Date.now();
        return true;
      }
      console.log('⛔ Circuit breaker: OPEN - blocking connection attempt');
      return false;
    }
    
    if (this.state === CircuitState.HALF_OPEN) {
      return true;
    }
    
    return false;
  }

  /**
   * Record successful connection
   */
  recordSuccess() {
    this.failureCount = 0;
    
    if (this.state === CircuitState.HALF_OPEN) {
      this.successCount++;
      console.log(`✅ Circuit breaker: Success in HALF_OPEN (${this.successCount}/${this.successThreshold})`);
      
      if (this.successCount >= this.successThreshold) {
        console.log('✅ Circuit breaker: Transitioning to CLOSED (service recovered)');
        this.state = CircuitState.CLOSED;
        this.successCount = 0;
        this.lastStateChange = Date.now();
      }
    }
  }

  /**
   * Record failed connection
   */
  recordFailure() {
    this.failureCount++;
    this.successCount = 0;
    
    console.log(`❌ Circuit breaker: Failure recorded (${this.failureCount}/${this.failureThreshold})`);
    
    if (this.state === CircuitState.HALF_OPEN) {
      console.log('❌ Circuit breaker: Failure in HALF_OPEN - transitioning back to OPEN');
      this.state = CircuitState.OPEN;
      this.nextAttempt = Date.now() + this.timeout;
      this.lastStateChange = Date.now();
    } else if (this.failureCount >= this.failureThreshold) {
      console.log(`⛔ Circuit breaker: Transitioning to OPEN (too many failures)`);
      this.state = CircuitState.OPEN;
      this.nextAttempt = Date.now() + this.timeout;
      this.lastStateChange = Date.now();
    }
  }

  /**
   * Reset circuit breaker
   */
  reset() {
    console.log('🔄 Circuit breaker: Manual reset');
    this.state = CircuitState.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.lastStateChange = Date.now();
  }

  /**
   * Get current state info
   */
  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      nextAttempt: this.nextAttempt,
      lastStateChange: this.lastStateChange,
      timeUntilNextAttempt: Math.max(0, this.nextAttempt - Date.now())
    };
  }
}

/**
 * Connection Quality Monitor
 * 
 * Tracks connection health metrics:
 * - Latency (ping/pong round-trip time)
 * - Packet loss (missed heartbeats)
 * - Connection stability (disconnections per hour)
 */
export class ConnectionQualityMonitor {
  constructor(options = {}) {
    this.windowSize = options.windowSize || 10; // Track last 10 measurements
    this.latencies = [];
    this.packetLosses = [];
    this.disconnections = [];
    this.lastPingTime = null;
    this.missedHeartbeats = 0;
    this.totalHeartbeats = 0;
  }

  /**
   * Record ping sent
   */
  recordPingSent() {
    this.lastPingTime = Date.now();
  }

  /**
   * Record pong received
   * 🔥 PHASE 4 OPTIMIZATION: Support latency parameter from Socket.IO engine
   *
   * @param {number} latency - Optional latency in milliseconds (from Socket.IO engine pong event)
   */
  recordPongReceived(latency) {
    let recordedLatency;

    // If latency is provided (from Socket.IO engine), use it directly
    if (latency !== undefined && latency !== null) {
      recordedLatency = latency;
    }
    // Otherwise, calculate from lastPingTime (legacy custom ping/pong)
    else if (this.lastPingTime) {
      recordedLatency = Date.now() - this.lastPingTime;
      this.lastPingTime = null;
    } else {
      // No latency data available
      return;
    }

    this.latencies.push(recordedLatency);

    // Keep only last N measurements
    if (this.latencies.length > this.windowSize) {
      this.latencies.shift();
    }

    this.totalHeartbeats++;

    console.log(`📊 Connection quality: Latency ${recordedLatency}ms`);
  }

  /**
   * Record missed heartbeat
   */
  recordMissedHeartbeat() {
    this.missedHeartbeats++;
    this.totalHeartbeats++;
    
    const packetLoss = this.totalHeartbeats > 0 
      ? (this.missedHeartbeats / this.totalHeartbeats) * 100 
      : 0;
    
    this.packetLosses.push(packetLoss);
    
    // Keep only last N measurements
    if (this.packetLosses.length > this.windowSize) {
      this.packetLosses.shift();
    }
    
    console.log(`📊 Connection quality: Missed heartbeat (${packetLoss.toFixed(1)}% packet loss)`);
  }

  /**
   * Record disconnection
   */
  recordDisconnection() {
    this.disconnections.push(Date.now());
    
    // Keep only disconnections from last hour
    const oneHourAgo = Date.now() - 3600000;
    this.disconnections = this.disconnections.filter(time => time > oneHourAgo);
    
    console.log(`📊 Connection quality: Disconnection recorded (${this.disconnections.length} in last hour)`);
  }

  /**
   * Get average latency
   */
  getAverageLatency() {
    if (this.latencies.length === 0) return 0;
    return this.latencies.reduce((sum, lat) => sum + lat, 0) / this.latencies.length;
  }

  /**
   * Get current packet loss percentage
   */
  getPacketLoss() {
    if (this.packetLosses.length === 0) return 0;
    return this.packetLosses[this.packetLosses.length - 1];
  }

  /**
   * Get disconnections per hour
   */
  getDisconnectionsPerHour() {
    return this.disconnections.length;
  }

  /**
   * Determine connection quality
   */
  getQuality() {
    const avgLatency = this.getAverageLatency();
    const packetLoss = this.getPacketLoss();
    
    if (avgLatency < 100 && packetLoss < 1) {
      return ConnectionQuality.EXCELLENT;
    } else if (avgLatency < 300 && packetLoss < 5) {
      return ConnectionQuality.GOOD;
    } else if (avgLatency < 1000 && packetLoss < 20) {
      return ConnectionQuality.FAIR;
    } else {
      return ConnectionQuality.POOR;
    }
  }

  /**
   * Get detailed metrics
   */
  getMetrics() {
    return {
      quality: this.getQuality(),
      averageLatency: Math.round(this.getAverageLatency()),
      packetLoss: this.getPacketLoss().toFixed(1),
      disconnectionsPerHour: this.getDisconnectionsPerHour(),
      totalHeartbeats: this.totalHeartbeats,
      missedHeartbeats: this.missedHeartbeats
    };
  }

  /**
   * Reset metrics
   */
  reset() {
    this.latencies = [];
    this.packetLosses = [];
    this.disconnections = [];
    this.missedHeartbeats = 0;
    this.totalHeartbeats = 0;
  }
}

/**
 * Calculate exponential backoff delay with jitter
 * 
 * Jitter prevents "thundering herd" problem where many clients
 * reconnect at exactly the same time.
 * 
 * @param {number} attempt - Current attempt number (0-based)
 * @param {number} baseDelay - Base delay in milliseconds
 * @param {number} maxDelay - Maximum delay in milliseconds
 * @param {number} jitterFactor - Jitter factor (0-1, default 0.3 = 30%)
 * @returns {number} Delay in milliseconds
 */
export function calculateBackoffWithJitter(attempt, baseDelay = 1000, maxDelay = 30000, jitterFactor = 0.3) {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  
  // Cap at max delay
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  
  // Add jitter: random value between (1 - jitterFactor) and (1 + jitterFactor)
  // Example: jitterFactor=0.3 means ±30% randomness
  const jitter = 1 + (Math.random() * 2 - 1) * jitterFactor;
  const delayWithJitter = cappedDelay * jitter;
  
  return Math.round(delayWithJitter);
}

/**
 * Adaptive reconnection strategy
 * 
 * Adjusts reconnection behavior based on connection quality:
 * - EXCELLENT/GOOD: Fast reconnection (short delays)
 * - FAIR: Normal reconnection (standard delays)
 * - POOR: Slow reconnection (longer delays)
 */
export function getAdaptiveReconnectDelay(attempt, quality, baseDelay = 1000, maxDelay = 30000) {
  let adjustedBaseDelay = baseDelay;
  
  switch (quality) {
    case ConnectionQuality.EXCELLENT:
    case ConnectionQuality.GOOD:
      // Fast reconnection for good connections
      adjustedBaseDelay = baseDelay * 0.5;
      break;
    case ConnectionQuality.FAIR:
      // Normal reconnection
      adjustedBaseDelay = baseDelay;
      break;
    case ConnectionQuality.POOR:
      // Slow reconnection for poor connections
      adjustedBaseDelay = baseDelay * 2;
      break;
  }
  
  return calculateBackoffWithJitter(attempt, adjustedBaseDelay, maxDelay);
}

export default {
  CircuitBreaker,
  ConnectionQualityMonitor,
  CircuitState,
  ConnectionQuality,
  calculateBackoffWithJitter,
  getAdaptiveReconnectDelay
};

